import { Card, Heading, Inline, Stack, Text, textVariants, vars } from '@etg/wings';
import { Accordion, useAccordion } from '@eti/components';
import { css } from '@eti/styles';
import { useTranslation } from '@eti/providers';
import { constants } from '@eti/utils';
import { getAgeTypeLabel } from '../../../../utils/travelers';
import { useOrderDetails } from '../../containers/OrderDetailsContext';
import DocumentLinks from '../common/DocumentLinks';
import ETickets from '../common/ETickets';
import TitledCard from '../../../../common/components/TitledCard';
import type { BoundType } from '../../models';

const accordionWrapperStyles = css`
  border: 1px solid ${vars.colors.divider};
  border-radius: ${vars.card.borderRadius};
`;

const inlineStyles = css`
  justify-content: space-between;
`;

const lightColoredTextStyles = css`
  color: #737373;
`;

interface ETicketsWrapperProps {
  bound: BoundType;
  passengerId?: string | null;
}

const ETicketsWrapper = ({ bound, passengerId }: ETicketsWrapperProps) => {
  const { isAnimationCompleted } = useAccordion();
  return (
    <ETickets
      bound={bound}
      isContentReady={isAnimationCompleted}
      key={bound.boundId}
      passengerId={passengerId}
    />
  );
};

const ImportantInfo = () => {
  const { t } = useTranslation();
  const { bounds, travelers } = useOrderDetails();
  return (
    <TitledCard
      data-testid="important-info-section"
      headingLevel={2}
      title={t('CheckIn.ImportantInfo.Title')}
    >
      <Stack spacing={24}>
        <Text>{t('CheckIn.ImportantInfo.Description')}</Text>
        <Stack>
          <DocumentLinks />
          <Text asHtml>{t('CheckIn.ImportantInfo.VisaRequirements.Text')}</Text>
        </Stack>
        <Card className={accordionWrapperStyles}>
          <Accordion id="passenger-tickets-accordion" spacing={16} variant="full-width">
            <Accordion.Header data-testid="passenger-tickets-accordion-header">
              <Stack spacing={4}>
                <Heading as="h3" level={3}>
                  {t('OrderDetails.Passenger.Drawer.ETicket.Title')}
                </Heading>
                <Text>{t('CheckIn.ImportantInfo.ETicket.Text')}</Text>
              </Stack>
            </Accordion.Header>
            <Accordion.Body data-testid="passenger-tickets-accordion-body">
              <Card as="ul" data-testid="passenger-tickets" padding={0}>
                {travelers.map((traveler) => (
                  <Card.Section as="li" key={traveler.id}>
                    <Inline alignY="center" className={inlineStyles} noWrap>
                      <Text
                        className={constants.MOUSE_FLOW_CLASS_NAMES}
                        variant={textVariants._16_BOLD}
                      >
                        {traveler.name}
                      </Text>
                      <Text className={lightColoredTextStyles}>
                        {t(getAgeTypeLabel(traveler.ageType))}
                      </Text>
                    </Inline>
                    {bounds.map((bound) => (
                      <ETicketsWrapper
                        bound={bound}
                        key={bound.boundId}
                        passengerId={traveler.id}
                      />
                    ))}
                  </Card.Section>
                ))}
              </Card>
            </Accordion.Body>
          </Accordion>
        </Card>
      </Stack>
    </TitledCard>
  );
};

export default ImportantInfo;
